/** @sfdc-extension-file SNK */
'use client';

import { Suspense, useMemo, type ComponentProps } from 'react';
import type { ComponentDesignMetadata } from '@salesforce/storefront-next-runtime/design/react';
import { usePageDesignerMode } from '@salesforce/storefront-next-runtime/design/react/core';
import { Region, type ComponentType } from '@/components/region';
import { Component as RenderComponent } from '@/components/region/component';
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
import { Carousel } from '@/shared-ui/organisms';
import { cn } from '@/lib/utils';

export const TRENDING_CAROUSEL_ITEMS_REGION_ID = 'items';

const trendingCarouselDefaults = {
    heading: 'TRENDING',
} as const;

interface TrendingCarouselProps extends ComponentProps<'div'> {
    /** Heading shown above the carousel. */
    heading?: string;

    regionId?: string;
    component?: ComponentType;
    componentData?: Record<string, Promise<unknown>>;
    designMetadata?: ComponentDesignMetadata;
    data?: unknown;
}

/* v8 ignore start - do not test decorators in unit tests, decorator functionality is tested separately*/
@Component('trendingCarousel', {
    name: 'Trending Carousel',
    description:
        'Horizontally scrolling carousel of Trending Item components built on `@/shared-ui/organisms` Carousel, matching the burger menu trending design.',
})
@RegionDefinition([
    {
        id: TRENDING_CAROUSEL_ITEMS_REGION_ID,
        name: 'Trending items',
        description: 'Add Trending Item components in the order they should appear in the carousel.',
        componentTypeInclusions: ['trendingItem'],
    },
])
export class TrendingCarouselMetadata {
    @AttributeDefinition({
        id: 'heading',
        name: 'Heading',
        description: 'Heading text shown above the trending carousel.',
        type: 'string',
        defaultValue: trendingCarouselDefaults.heading,
    })
    heading?: string;
}
/* v8 ignore stop */

export default function TrendingCarousel({
    className,
    heading = trendingCarouselDefaults.heading,
    regionId: _regionId,
    component,
    componentData: _componentData,
    designMetadata: _designMetadata,
    data: _data,
    ...props
}: TrendingCarouselProps) {
    const { isDesignMode, isPreviewMode } = usePageDesignerMode();
    const isPageDesignerActive = isDesignMode || isPreviewMode;

    const componentWithRegionMetadata = useMemo(
        () => (component ? withComponentRegionDesignMetadata(component, TrendingCarouselMetadata) : undefined),
        [component]
    );

    if (!componentWithRegionMetadata) {
        return null;
    }

    const itemsRegion = componentWithRegionMetadata.regions?.find(
        (region) => region.id === TRENDING_CAROUSEL_ITEMS_REGION_ID
    );
    const items = itemsRegion?.components ?? [];

    if (!itemsRegion || (items.length === 0 && !isPageDesignerActive)) {
        return null;
    }

    if (items.length === 0) {
        return (
            <div className={cn('mx-auto w-full max-w-[390px] overflow-hidden pb-4', className)} {...props}>
                <Carousel.Root ariaLabel={`${heading} scroller`}>
                    <Carousel.Heading
                        className="px-4 pt-6 tracking-[0.135rem] text-[18px] leading-[22px] font-normal"
                        variant="h3">
                        {heading}
                    </Carousel.Heading>
                    <Suspense fallback={null}>
                        <Region
                            component={componentWithRegionMetadata}
                            regionId={TRENDING_CAROUSEL_ITEMS_REGION_ID}
                            errorElement={null}
                        />
                    </Suspense>
                </Carousel.Root>
            </div>
        );
    }

    return (
        <div className={cn('mx-auto w-full max-w-[390px] overflow-hidden pb-4', className)} {...props}>
            <Carousel.Root ariaLabel={`${heading} scroller`}>
                <Carousel.Heading
                    className="px-4 pt-6 tracking-[0.135rem] text-[18px] leading-[22px] font-normal"
                    variant="h3">
                    {heading}
                </Carousel.Heading>
                <Carousel.Content
                    className="pl-4"
                    mobileLayout="fullwidth"
                    desktopLayout="fullwidth"
                    mobileGap={16}
                    desktopGap={16}
                    itemsClassName="!w-auto !basis-auto">
                    {items.map((item) => {
                        const typedItem = item as ComponentType;
                        return (
                            <RenderComponent
                                key={typedItem.contentLinkUuid ?? typedItem.id}
                                component={typedItem}
                                regionId={itemsRegion.id}
                            />
                        );
                    })}
                </Carousel.Content>
            </Carousel.Root>
        </div>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export { TrendingCarouselPdSkeleton, TrendingCarouselPdSkeleton as fallback } from './skeleton';
